'use client';

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Search, MapPin, Briefcase, Loader, CheckCircle2, XCircle, ExternalLink, Sparkles,
  TrendingUp, TrendingDown, Minus, BarChart3, Zap,
} from 'lucide-react';
import { runLeadScraper } from '../../redux/crmSlice';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import Tabs from '../../components/ui/Tabs';

import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';
const BUSINESS_TYPES = [
  'Restaurant', 'Hotel', 'Salon', 'Gym', 'Clinic', 'Dentist', 'Real Estate Agency',
  'Law Firm', 'Retail Store', 'Auto Repair Shop', 'Accounting Firm', 'Interior Designer',
  'Photography Studio', 'Educational Institute', 'IT Services', 'Manufacturing',
];

const COUNTRIES = [
  { code: 'IN', name: 'India' }, { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' }, { code: 'AE', name: 'UAE' },
  { code: 'AU', name: 'Australia' }, { code: 'CA', name: 'Canada' },
  { code: 'SG', name: 'Singapore' }, { code: 'DE', name: 'Germany' },
];

const TABS = [
  { id: 'demand', label: 'Market Demand', icon: BarChart3 },
  { id: 'scraper', label: 'Lead Scraper', icon: Sparkles },
];

export default function LeadScraperPage() {
  const [activeTab, setActiveTab] = useState('demand');
  const [prefill, setPrefill] = useState(null);

  return (
    <div className={PAGE}>
      <PageHeader
        title="Lead Generation"
        subtitle="Find where demand for a service is highest, then scrape and qualify businesses in that market."
        icon={Sparkles}
      />

      <Tabs tabs={TABS.map((t) => ({ key: t.id, label: t.label, icon: t.icon }))} value={activeTab} onChange={setActiveTab} />

      {activeTab === 'demand' && (
        <MarketDemandTab
          onScrapeHere={(location, businessType) => {
            setPrefill({ location, businessType });
            setActiveTab('scraper');
          }}
        />
      )}
      {activeTab === 'scraper' && <ScraperTab prefill={prefill} />}
    </div>
  );
}

function MarketDemandTab({ onScrapeHere }) {
  const [keyword, setKeyword] = useState('');
  const [geo, setGeo] = useState('IN');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!keyword.trim()) return alertService.error('Missing Info', 'Please enter a keyword to analyze.');

    setLoading(true);
    setData(null);
    try {
      const res = await api.post('/crm/market-demand/analyze', { keyword, geo });
      setData(res.data);
    } catch (err) {
      alertService.error('Analysis Failed', err.response?.data?.error || 'Could not fetch search demand data.');
    } finally {
      setLoading(false);
    }
  };

  const TrendIcon = data?.trend === 'rising' ? TrendingUp : data?.trend === 'falling' ? TrendingDown : Minus;
  const trendColor = data?.trend === 'rising' ? '#2E844A' : data?.trend === 'falling' ? '#BA0517' : '#706E6B';

  return (
    <div className="space-y-6">
      <div className="sf-card">
        <div className="sf-card-header">
          <h2 className="sf-card-title flex items-center gap-2">
            <BarChart3 size={16} className="text-[#0176D3]" /> Search Demand by Region
          </h2>
        </div>
        <div className="sf-card-body">
          <p className="text-[12px] text-[#706E6B] mb-4">
            Shows relative Google search interest for a service by region — aggregate demand data, not individual searchers. Use it to decide where to run the Lead Scraper.
          </p>
          <form onSubmit={handleAnalyze} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div className="md:col-span-2 space-y-1">
              <label className="text-[11px] font-semibold text-[#706E6B]">Keyword / Service</label>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="e.g. web design services"
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]/10 transition-all"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-[#706E6B]">Country</label>
              <select
                value={geo}
                onChange={(e) => setGeo(e.target.value)}
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
              >
                {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <button
                type="submit"
                disabled={loading}
                className="h-9 px-6 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] transition-all flex items-center gap-2 disabled:opacity-60 whitespace-nowrap shrink-0"
              >
                {loading ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
                {loading ? 'Analyzing…' : 'Analyze'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {data && (
        <div className="sf-card overflow-hidden">
          <div className="sf-card-header">
            <h2 className="sf-card-title">
              Top Regions for "{data.keyword}" in {data.country}
            </h2>
            <span className="flex items-center gap-1.5 text-[12px] font-bold" style={{ color: trendColor }}>
              <TrendIcon size={14} /> {data.trend === 'flat' ? 'Stable' : `${data.trend} ${data.changePct}%`} (12mo)
            </span>
          </div>
          <div className="sf-table-container border-0">
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Region</th>
                  <th>Relative Interest</th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.topRegions.length > 0 ? data.topRegions.map((r) => (
                  <tr key={r.geoCode}>
                    <td className="font-bold text-[#080707]">{r.region}</td>
                    <td>
                      <div className="flex items-center gap-2 w-40">
                        <div className="flex-1 h-1.5 bg-[#F3F2F2] rounded-full overflow-hidden border border-[#D8DDE6]">
                          <div className="h-full bg-[#0176D3]" style={{ width: `${r.interest}%` }} />
                        </div>
                        <span className="text-[11px] font-bold text-[#706E6B] tabular-nums w-8">{r.interest}</span>
                      </div>
                    </td>
                    <td className="text-right">
                      <button
                        onClick={() => onScrapeHere(`${r.region}, ${data.country}`, data.keyword)}
                        className="h-8 px-3 bg-white border border-[#C9C9C9] rounded text-[11px] font-bold text-[#0176D3] hover:bg-[#EAF4FE] transition-all inline-flex items-center gap-1.5 whitespace-nowrap shrink-0"
                      >
                        <Zap size={12} /> Scrape Leads Here
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={3} className="py-12 text-center text-[#706E6B] text-sm">
                      No regional data available for this keyword.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!data && !loading && (
        <div className="sf-card p-12 text-center text-[#706E6B]">
          <BarChart3 size={40} className="mx-auto mb-3 opacity-20" />
          <p className="text-sm font-medium">Enter a keyword and country to see where demand is highest.</p>
        </div>
      )}
    </div>
  );
}

function ScraperTab({ prefill }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    location: prefill?.location || '',
    businessType: prefill?.businessType || '',
    limit: 10,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    if (prefill) {
      setForm(prev => ({ ...prev, location: prefill.location, businessType: prefill.businessType }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleRun = async (e) => {
    e.preventDefault();
    if (!form.location.trim() || !form.businessType.trim()) {
      return alertService.error('Missing Info', 'Please enter a location and a business type.');
    }

    setLoading(true);
    setResult(null);
    try {
      const res = await dispatch(runLeadScraper(form)).unwrap();
      setResult(res);
      alertService.success('Scrape Complete', res.message);
    } catch (err) {
      alertService.error('Scrape Failed', typeof err === 'string' ? err : 'Failed to run the lead scraper.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search Form */}
      <div className="sf-card">
        <div className="sf-card-header">
          <h2 className="sf-card-title flex items-center gap-2">
            <Search size={16} className="text-[#0176D3]" /> Search Criteria
          </h2>
        </div>
        <div className="sf-card-body">
          <form onSubmit={handleRun} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div className="md:col-span-2 space-y-1">
              <label className="text-[11px] font-semibold text-[#706E6B] flex items-center gap-1">
                <MapPin size={12} /> Location
              </label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="e.g. Mumbai, India"
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]/10 transition-all"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-[#706E6B] flex items-center gap-1">
                <Briefcase size={12} /> Business Type
              </label>
              <input
                type="text"
                list="business-type-suggestions"
                value={form.businessType}
                onChange={(e) => handleChange('businessType', e.target.value)}
                placeholder="e.g. Restaurant"
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]/10 transition-all"
              />
              <datalist id="business-type-suggestions">
                {BUSINESS_TYPES.map(t => <option key={t} value={t} />)}
              </datalist>
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-[#706E6B]">Max Results</label>
              <select
                value={form.limit}
                onChange={(e) => handleChange('limit', Number(e.target.value))}
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
              >
                {[5, 10, 15, 20].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>

            <div className="md:col-span-4">
              <button
                type="submit"
                disabled={loading}
                className="h-9 px-6 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] transition-all flex items-center gap-2 disabled:opacity-60 whitespace-nowrap shrink-0"
              >
                {loading ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
                {loading ? 'Scraping…' : 'Run Scraper'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sf-card p-4">
              <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider">Found</p>
              <p className="text-2xl font-bold text-[#080707]">{result.totalFound}</p>
            </div>
            <div className="sf-card p-4">
              <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider">New Leads Created</p>
              <p className="text-2xl font-bold text-[#2E844A]">{result.created.length}</p>
            </div>
            <div className="sf-card p-4">
              <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider">Skipped</p>
              <p className="text-2xl font-bold text-[#706E6B]">{result.skipped.length}</p>
            </div>
          </div>

          {result.created.length > 0 && (
            <div className="sf-card overflow-hidden">
              <div className="sf-card-header">
                <h2 className="sf-card-title">New Leads</h2>
              </div>
              <div className="sf-table-container border-0">
                <table className="sf-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Location</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Website</th>
                      <th>AI Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.created.map((lead) => (
                      <tr key={lead._id} className="cursor-pointer" onClick={() => navigate(`/dashboard/crm/leads/${lead._id}`)}>
                        <td className="font-bold text-[#0176D3]">{lead.name}</td>
                        <td className="text-[#706E6B]">{lead.location || '—'}</td>
                        <td>{lead.phoneNumber || '—'}</td>
                        <td>
                          {lead.email ? (
                            <span className="flex items-center gap-1.5">
                              {lead.email}
                              {lead.emailVerified ? (
                                <CheckCircle2 size={13} className="text-[#2E844A]" title="MX record verified" />
                              ) : (
                                <XCircle size={13} className="text-[#BA0517]" title="Could not verify domain" />
                              )}
                            </span>
                          ) : '—'}
                        </td>
                        <td>
                          {lead.website ? (
                            <a
                              href={lead.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-[#0176D3] hover:underline flex items-center gap-1"
                            >
                              Visit <ExternalLink size={11} />
                            </a>
                          ) : '—'}
                        </td>
                        <td className="max-w-xs">
                          <p className="text-[11px] text-[#706E6B] line-clamp-3 whitespace-pre-line">
                            {lead.notes?.[0]?.text || 'No remarks generated.'}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result.skipped.length > 0 && (
            <div className="sf-card">
              <div className="sf-card-header">
                <h2 className="sf-card-title">Skipped</h2>
              </div>
              <div className="sf-card-body space-y-2">
                {result.skipped.map((s, idx) => (
                  <div key={idx} className="flex items-center justify-between text-[12px] py-1 border-b border-[#F3F2F2] last:border-0">
                    <span className="font-medium text-[#080707]">{s.name}</span>
                    <span className="text-[#706E6B]">{s.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!result && (
        <div className={cn('sf-card p-12 text-center text-[#706E6B]')}>
          <Sparkles size={40} className="mx-auto mb-3 opacity-20" />
          <p className="text-sm font-medium">Enter a location and business type above to find and qualify new leads.</p>
        </div>
      )}
    </div>
  );
}
